/**
 * @fileoverview Navigation Store for Diagram Linking
 * 
 * Manages breadcrumb navigation state for drill-down navigation
 * when navigating between linked diagrams.
 * 
 * @see Issue #789 - Universal Diagram Linking System
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface BreadcrumbItem {
  diagramId: string;
  diagramName: string;
  folderId: string; // Store folderId for navigation
  sourceShapeId?: string; // The shape that was clicked to navigate here
}

interface NavigationState {
  /** Stack of diagrams we've drilled into (breadcrumb path) */
  breadcrumbStack: BreadcrumbItem[];
  
  /** Lock to prevent race conditions during navigation */
  isNavigating: boolean;
  
  /** Pending navigation (queued when navigation is in progress) */
  pendingNavigation: { diagramId: string; name: string; sourceShapeId?: string } | null;

  // Actions
  pushDiagram: (diagramId: string, name: string, folderId: string, sourceShapeId?: string) => void;
  popDiagram: () => void;
  navigateTo: (index: number) => void; // Click breadcrumb item to navigate back
  clearStack: () => void;
  setNavigating: (isNavigating: boolean) => void;
  setPendingNavigation: (pending: { diagramId: string; name: string; folderId: string; sourceShapeId?: string } | null) => void;
}

export const useNavigationStore = create<NavigationState>()(
  persist(
    (set) => ({
      breadcrumbStack: [],
      isNavigating: false,
      pendingNavigation: null,

      pushDiagram: (diagramId: string, name: string, folderId: string, sourceShapeId?: string) => {
        set((state) => {
          // Don't add if already at the top
          if (state.breadcrumbStack.length > 0 && 
              state.breadcrumbStack[state.breadcrumbStack.length - 1].diagramId === diagramId) {
            return state;
          }

          // Add to stack
          return {
            breadcrumbStack: [
              ...state.breadcrumbStack,
              { diagramId, diagramName: name, folderId, sourceShapeId },
            ],
          };
        });
      },

      popDiagram: () => {
        set((state) => {
          if (state.breadcrumbStack.length === 0) return state;
          return {
            breadcrumbStack: state.breadcrumbStack.slice(0, -1),
          };
        });
      },

      navigateTo: (index: number) => {
        set((state) => {
          if (index < 0 || index >= state.breadcrumbStack.length) return state;
          // Navigate to index by truncating stack to its ancestors (exclude current)
          return {
            breadcrumbStack: state.breadcrumbStack.slice(0, index),
          };
        });
      },

      clearStack: () => {
        set({
          breadcrumbStack: [],
          pendingNavigation: null,
        });
      },

      setNavigating: (isNavigating: boolean) => {
        set({ isNavigating });
      },

      setPendingNavigation: (pending: { diagramId: string; name: string; folderId: string; sourceShapeId?: string } | null) => {
        set({ pendingNavigation: pending });
      },
    }),
    {
      name: 'diagram-navigation-storage',
      partialize: (state) => ({
        // Only persist breadcrumb stack, not navigation state
        breadcrumbStack: state.breadcrumbStack,
      }),
      merge: (persistedState: any, currentState) => ({
        ...currentState,
        breadcrumbStack: persistedState.breadcrumbStack || [],
      }),
    }
  )
);
